/**
 * KPI API Documentation
 *
 * Inline documentation for the public KPI API — right where you need it (5S).
 * Shows endpoints, authentication, examples, and error codes.
 */

import { useState } from 'react';
import { Copy, Check, Terminal, Key, Zap, AlertTriangle, BookOpen } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

function CopyBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group">
      <pre className="bg-zinc-950 text-zinc-100 rounded-lg p-4 text-sm overflow-x-auto font-mono leading-relaxed">
        <code>{code}</code>
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-1.5 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200 opacity-0 group-hover:opacity-100 transition-opacity"
        title="Copy to clipboard"
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

function EndpointCard({
  method,
  path,
  scope,
  description,
  requestBody,
  responseBody,
  children,
}: {
  method: 'GET' | 'POST';
  path: string;
  scope: string;
  description: string;
  requestBody?: string;
  responseBody: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="bg-muted/50 px-4 py-3 flex items-center gap-3 border-b">
        <Badge variant={method === 'GET' ? 'secondary' : 'default'} className="font-mono text-xs">
          {method}
        </Badge>
        <code className="text-sm font-semibold">{path}</code>
        <Badge variant="outline" className="ml-auto text-xs">
          {scope}
        </Badge>
      </div>
      <div className="p-4 space-y-3">
        <p className="text-sm text-muted-foreground">{description}</p>
        {children}
        {requestBody && (
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1.5">Request Body</p>
            <CopyBlock code={requestBody} />
          </div>
        )}
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1.5">Response</p>
          <CopyBlock code={responseBody} />
        </div>
      </div>
    </div>
  );
}

export function KpiApiDocs() {
  const baseUrl = window.location.hostname === 'localhost'
    ? 'http://localhost:3098'
    : 'https://api.atek.io';

  return (
    <div className="space-y-6">
      {/* Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Public KPI API
          </CardTitle>
          <CardDescription>
            Push KPI values from external scripts, cron jobs, and integrations.
            KPIs are identified by their <code className="bg-muted px-1 rounded text-xs">code</code> field — a stable slug you set when creating the KPI.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
              <Key className="h-5 w-5 mt-0.5 text-muted-foreground shrink-0" />
              <div>
                <p className="text-sm font-medium">API Key Auth</p>
                <p className="text-xs text-muted-foreground">Pass <code className="bg-muted px-1 rounded">X-API-Key</code> header on every request</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
              <Zap className="h-5 w-5 mt-0.5 text-muted-foreground shrink-0" />
              <div>
                <p className="text-sm font-medium">Rate Limited</p>
                <p className="text-xs text-muted-foreground">60 requests/min per key (configurable)</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
              <Terminal className="h-5 w-5 mt-0.5 text-muted-foreground shrink-0" />
              <div>
                <p className="text-sm font-medium">JSON API</p>
                <p className="text-xs text-muted-foreground">All requests and responses use JSON</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Authentication */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Key className="h-4 w-4" />
            Authentication
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Generate an API key from <strong>Tools &gt; API Keys</strong>. Include it in every request:
          </p>
          <CopyBlock code={`curl ${baseUrl}/v1/kpi/definitions \\
  -H "X-API-Key: atek_your_api_key_here"`} />
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-500 shrink-0" />
            <p className="text-xs text-amber-700 dark:text-amber-400">
              The full API key is shown <strong>only once</strong> when created. Store it securely — it cannot be retrieved later.
            </p>
          </div>
          <div className="text-sm">
            <p className="font-medium mb-2">Scopes</p>
            <div className="grid gap-1.5">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="font-mono text-xs">kpi:read</Badge>
                <span className="text-sm text-muted-foreground">List KPI definitions and their codes</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="font-mono text-xs">kpi:write</Badge>
                <span className="text-sm text-muted-foreground">Push KPI values (upsert by code + date)</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Endpoints */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Terminal className="h-4 w-4" />
            Endpoints
          </CardTitle>
          <CardDescription>
            Base URL: <code className="bg-muted px-1.5 py-0.5 rounded text-xs">{baseUrl}</code>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* GET definitions */}
          <EndpointCard
            method="GET"
            path="/v1/kpi/definitions"
            scope="kpi:read"
            description="List all KPI definitions that have a code assigned. Use this to discover available KPI codes for your organisation."
            responseBody={`[
  {
    "id": "550e8400-e29b-41d4-a716-446655440000",
    "code": "sensor_count",
    "name": "Active Sensors",
    "category": "operations",
    "frequency": "monthly",
    "goal_unit": "sensors",
    "goal_value": "500.0000",
    "orientation_rule": "gte_goal"
  }
]`}
          />

          {/* POST values */}
          <EndpointCard
            method="POST"
            path="/v1/kpi/values"
            scope="kpi:write"
            description="Push a single KPI value. If a value already exists for the same code + period, it is replaced (upsert)."
            requestBody={`{
  "code": "sensor_count",
  "value": 342,
  "datetime": "2026-04-01",
  "notes": "Auto-sync from monitoring"
}`}
            responseBody={`{
  "success": true,
  "kpi": { "id": "...", "code": "sensor_count", "name": "Active Sensors" },
  "value": { "id": "...", "value": 342, "datetime": "2026-04-01" },
  "action": "created"
}`}
          >
            <div className="text-xs space-y-1 text-muted-foreground">
              <p><code className="bg-muted px-1 rounded">code</code> — required, lowercase alphanumeric + hyphens/underscores</p>
              <p><code className="bg-muted px-1 rounded">value</code> — required, number (up to 11 integer digits, 4 decimal places)</p>
              <p><code className="bg-muted px-1 rounded">datetime</code> — required, ISO 8601 date (e.g. "2026-04-01")</p>
              <p><code className="bg-muted px-1 rounded">notes</code> — optional, max 1000 characters</p>
            </div>
          </EndpointCard>

          {/* POST values/bulk */}
          <EndpointCard
            method="POST"
            path="/v1/kpi/values/bulk"
            scope="kpi:write"
            description="Push multiple KPI values at once (max 50). Each item is processed independently — partial success is possible."
            requestBody={`{
  "values": [
    { "code": "sensor_count", "value": 342, "datetime": "2026-04-01" },
    { "code": "inactivity_rate", "value": 3.2, "datetime": "2026-04-01" },
    { "code": "ticket_count", "value": 12, "datetime": "2026-04-01" }
  ]
}`}
            responseBody={`{
  "results": [
    { "code": "sensor_count", "success": true, "value_id": "..." },
    { "code": "inactivity_rate", "success": true, "value_id": "..." },
    { "code": "ticket_count", "success": false, "error": "KPI not found" }
  ],
  "total": 3,
  "succeeded": 2,
  "failed": 1
}`}
          />
        </CardContent>
      </Card>

      {/* Quick Start */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Quick Start</CardTitle>
          <CardDescription>Copy-paste example for a monthly cron job</CardDescription>
        </CardHeader>
        <CardContent>
          <CopyBlock code={`#!/bin/bash
# Push monthly KPI values via ATEK Public API
# Run via cron: 0 8 1 * * /path/to/push-kpis.sh

API_KEY="atek_your_key_here"
API_URL="${baseUrl}/v1/kpi/values/bulk"
DATE=$(date +%Y-%m-01)

curl -s -X POST "$API_URL" \\
  -H "X-API-Key: $API_KEY" \\
  -H "Content-Type: application/json" \\
  -d "{
    \\"values\\": [
      { \\"code\\": \\"sensor_count\\", \\"value\\": $(get_sensor_count), \\"datetime\\": \\"$DATE\\" },
      { \\"code\\": \\"inactivity_rate\\", \\"value\\": $(get_inactivity), \\"datetime\\": \\"$DATE\\" }
    ]
  }"
`} />
        </CardContent>
      </Card>

      {/* Rate Limits & Errors */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Rate Limits & Error Codes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-sm font-medium mb-2">Rate Limit Headers</p>
            <div className="text-xs space-y-1 text-muted-foreground font-mono">
              <p>X-RateLimit-Limit: 60</p>
              <p>X-RateLimit-Remaining: 58</p>
              <p>X-RateLimit-Reset: 1712188800</p>
            </div>
          </div>
          <div>
            <p className="text-sm font-medium mb-2">Error Responses</p>
            <div className="rounded-md border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="text-left px-3 py-2 font-medium">Status</th>
                    <th className="text-left px-3 py-2 font-medium">Meaning</th>
                    <th className="text-left px-3 py-2 font-medium">Example</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="px-3 py-2"><Badge variant="destructive" className="text-xs">400</Badge></td>
                    <td className="px-3 py-2 text-muted-foreground">Invalid request body or parameters</td>
                    <td className="px-3 py-2 font-mono text-xs">{"{"}&quot;error&quot;:&quot;Validation failed&quot;{"}"}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2"><Badge variant="destructive" className="text-xs">401</Badge></td>
                    <td className="px-3 py-2 text-muted-foreground">Missing or invalid API key</td>
                    <td className="px-3 py-2 font-mono text-xs">{"{"}&quot;error&quot;:&quot;Invalid API key&quot;{"}"}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2"><Badge variant="destructive" className="text-xs">403</Badge></td>
                    <td className="px-3 py-2 text-muted-foreground">Key lacks required scope</td>
                    <td className="px-3 py-2 font-mono text-xs">{"{"}&quot;error&quot;:&quot;Insufficient scope&quot;{"}"}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2"><Badge variant="destructive" className="text-xs">404</Badge></td>
                    <td className="px-3 py-2 text-muted-foreground">KPI code not found</td>
                    <td className="px-3 py-2 font-mono text-xs">{"{"}&quot;error&quot;:&quot;KPI not found&quot;{"}"}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2"><Badge variant="secondary" className="text-xs">429</Badge></td>
                    <td className="px-3 py-2 text-muted-foreground">Rate limit exceeded</td>
                    <td className="px-3 py-2 font-mono text-xs">{"{"}&quot;error&quot;:&quot;Rate limit exceeded&quot;, &quot;retry_after&quot;:30{"}"}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
